import { isEqual } from 'lodash';
import Quagga, { QuaggaJSCodeReader, QuaggaJSReaderConfig, QuaggaJSResultCallbackFunction, QuaggaJSResultObject } from '@ericblade/quagga2';
import { Observable, Subject, Subscriber } from 'rxjs';
import { bufferCount, distinctUntilChanged, map } from 'rxjs/operators';

import { Result as ZxingResult } from '@zxing/library'
import { BrowserDatamatrixCodeReader, BrowserQRCodeReader } from '@zxing/browser'

declare const QUAGGA_FREQUENCY: number
declare const QUAGGA_USE_HALF_SAMPLE: boolean
declare const QUAGGA_NB_WORKERS: number
declare const QUAGGA_LOCATE: boolean
declare const CAMERA_SCANNER_QR_ENABLED: boolean
declare const CAMERA_SCANNER_DATA_MATRIX_ENABLED: boolean
declare const CAMERA_SCANNER_1D_CODES_ENABLED: boolean

export const PREFERRED_VIDEO_DEVICE_ID = "preferredVideoDeviceId";
export const VIDEO_DEVICES_IDS = "videoDevicesIds";

export interface Result {
    box: any;
    boxes: any;
    codeResult: {
        code: string;
    };
}

/**
 * This class is used to decode barcodes 
 * It uses first the Quagga.js lib to look for 1D barcodes, if no such one is
 * found, a lookup is made with the Zxing library to find 2D barcodes (QRCode and DataMatrix)
 */
export class CameraScanner extends Observable<string> {

    get torch() {
        return this._torch;
    }

    public get hasSeveralDevices() {
        return this.videoDevices && this.videoDevices.length > 1;
    }
    private track: MediaStreamTrack | null;
    // tslint:disable-next-line:variable-name
    private _torch = false;
    private subscriber: Subscriber<string>;
    private queue$ = new Subject<QuaggaJSResultObject>();
    private readers: (QuaggaJSReaderConfig | QuaggaJSCodeReader)[];

    // Size preferences for media device seeking 
    private maxWidth: any = window.innerWidth
    private maxHeight: any = window.innerHeight
    
    private videoCssSelector?: string

    // The index of the preferred device in the list below
    private deviceToUseIndex: number = 0;

    // An array of all available media devices (as returned by enumerateDevices())
    private videoDevices: any[] = [];

    private qrCodeReader: BrowserQRCodeReader
    private dataMatrixReader: BrowserDatamatrixCodeReader

    private code2DActivated: boolean
    private code1DActivated: boolean

    // Callbacks to execute after start and before stop
    // tslint:disable-next-line:no-empty
    private onStart = () => { }
    // tslint:disable-next-line:no-empty
    private onStop = () => { }

    constructor(readers: (QuaggaJSReaderConfig | QuaggaJSCodeReader)[], onStart, onStop, maxWidth?: number, maxHeight?: number, videoCssSelector?: string) {
        super(o => {
            this.subscriber = o;
        });

        this.code1DActivated = CAMERA_SCANNER_1D_CODES_ENABLED
        this.code2DActivated = CAMERA_SCANNER_QR_ENABLED || CAMERA_SCANNER_DATA_MATRIX_ENABLED

        this.videoCssSelector = videoCssSelector

        this.onStart = onStart;
        this.onStop = onStop;

        if (maxWidth) {
            this.maxWidth = maxWidth
        }

        if (maxHeight) {
            this.maxHeight = maxHeight
        }

        this.qrCodeReader = new BrowserQRCodeReader()
        this.dataMatrixReader = new BrowserDatamatrixCodeReader()
        this.readers = readers;
        this.queue$
            .pipe(
                bufferCount(2),
                distinctUntilChanged(
                    // we use !isEqual instead of isEqual because we want to emit only if previous and next are equal.
                    // We inverse the operator behavior.
                    (previous: QuaggaJSResultObject[], next: QuaggaJSResultObject[]) => !isEqual(previous, next),
                ),
                map(([result]: QuaggaJSResultObject[]) => result.codeResult.code),
            )
            .subscribe(code => this.subscriber.next(code ?? undefined));
    }

    public async toggleTorch() {
        // Turn torch on
        if (!this._torch) {
            try {
                await this.applyTrackConstraints({
                    advanced: [{ torch: true } as any], // setting it to false to turn off doesn't work
                });
                this._torch = !this._torch;
            } catch (error) {
                // do nothing
            }

        }
        // Turn torch off, for now we are forced to stop the video stream and restart it
        // Maybe there will be a better way to do it in the future, stay tuned !
        else {
            this.stop();
            this.start();
            this._torch = !this._torch;
        }
    }

    public async start() {
        try{
            await this.stop()
        } catch(err) {
            //console.log(err)
        }

        console.info("CameraScanner: starting...")
        Quagga.onProcessed(this.onProcessed);
        Quagga.onDetected(this.onDetected);

        console.info("CameraScanner: init devices...")
        await this.initDevices();

        const videoDeviceId = localStorage.getItem(PREFERRED_VIDEO_DEVICE_ID);
        return new Promise<void>((resolve, reject) => {
            const constraints = {
                deviceId: videoDeviceId,
                facingMode: 'environment',
                height: this.maxHeight,
                width: (this.maxWidth * 0.8)
            } as MediaTrackConstraints

            console.info("CameraScanner: quagga init...")
            Quagga.init(
                {
                    decoder: {
                        readers: this.readers,
                    },
                    frequency: QUAGGA_FREQUENCY,
                    inputStream: {
                        constraints,
                        type: 'LiveStream',
                        target: this.videoCssSelector,
                        willReadFrequently: true,
                    },
                    locate: QUAGGA_LOCATE,
                    locator: {
                        halfSample: QUAGGA_USE_HALF_SAMPLE,
                        willReadFrequently: true,
                    },
                    numOfWorkers: QUAGGA_NB_WORKERS,
                },
                async (error: Error) => {
                    if (error) {
                        console.info("CameraScanner: Quagga init error: ", error)
                        reject(error); // logger ?
                    }

                    try {
                        console.info("CameraScanner: getting track...")
                        this.track = Quagga.CameraAccess.getActiveTrack();
                        //console.info("CameraScanner: track: ", this.track)
    
                        console.info("CameraScanner: starting Quagga...")
                        await Quagga.start();
                        console.info("CameraScanner: Quagga started")
                        resolve();
                        if (this.onStart !== undefined) {
                            //console.info("CameraScanner: execute start callback")
                            this.onStart()
                        }
                    } catch(err) {
                        console.info("CameraScanner: error: ", err)
                        reject(err)
                    }
                },
            );
        });
    }

    public resetCallbacks() {
        console.info("CameraScanner: Resetting callbacks...")
        // tslint:disable-next-line:no-empty
        this.onStart = () => { }
        // tslint:disable-next-line:no-empty
        this.onStop = () => { }
    }

    public async stop() {
        console.info("CameraScanner: stoping...")
        Quagga.offProcessed(this.onProcessed);
        Quagga.offDetected(this.onDetected);
        Quagga.CameraAccess.release()

        this.onStop();

        const res = await Quagga.stop();
        console.info("CameraScanner: stopped")
        return res
    }

    public goToNextCameraDevice() {
        console.info("CameraScanner: switching camera...")
        this.stop()
        this.deviceToUseIndex = (this.deviceToUseIndex + 1) % this.videoDevices.length;
        this.updatePreferredVideoDeviceIndex();
        this.start();
    }

    /**
     * Updates the id of the preferred device in localStorage
     */
    private updatePreferredVideoDeviceIndex() {
        const preferredDeviceId = this.videoDevices[this.deviceToUseIndex]?.deviceId
        if(preferredDeviceId) {
            localStorage.setItem(PREFERRED_VIDEO_DEVICE_ID, preferredDeviceId)
        }
    }

    private async applyTrackConstraints(constraints: Record<string, any>) {
        if (this.track && typeof this.track.applyConstraints === 'function') {
            await this.track.applyConstraints(constraints);
            return true;
        }

        throw new Error('applyConstraint is not compatible with this platform');
    }

    private onDetected: QuaggaJSResultCallbackFunction = (result: QuaggaJSResultObject) => this.queue$.next(result);

    private onProcessed = result => {
        if(this.code1DActivated) {
            if(result && result.codeResult) {
                this.draw1DBarcodeBounds(result)
                return
            }
        }

        if(this.code2DActivated) {
            this.lookFor2DBarcodes()
        }
    }

    private zxingResultToQuaggaResult(zxingRes: ZxingResult): QuaggaJSResultObject {
        return {
            codeResult: {
                    code: zxingRes.getText(),
                    start: 0,
                    end: 0,
                    codeset: 0,
                    startInfo: {
                        error: 0,
                        code: 0,
                        start: 0,
                        end: 0
                    },
                    decodedCodes: [],
                    endInfo: {
                        error: 0,
                        code: 0,
                        start: 0,
                        end: 0
                    },
                    direction: 0,
                    format: ''
            },
            line: [],
            angle: 0,
            pattern: [],
            box: [],
            boxes: []
        }
    }

    private lookFor2DBarcodes() {
        if(CAMERA_SCANNER_QR_ENABLED) {
            try {
                const result = this.qrCodeReader.decodeFromCanvas(Quagga.canvas.ctx.image.canvas)
                const res: QuaggaJSResultObject = this.zxingResultToQuaggaResult(result)
                this.queue$.next(res)
                return
            } catch(err) {
                //console.log("CameraScanner: QR code error")
                console.error(err)
            }
        }
        
        if(CAMERA_SCANNER_DATA_MATRIX_ENABLED) {
            try {
                const result = this.qrCodeReader.decodeFromCanvas(Quagga.canvas.ctx.image.canvas)
                const res: QuaggaJSResultObject = this.zxingResultToQuaggaResult(result)
                this.queue$.next(res)
            } catch(err) {
                //console.log("CameraScanner: Data matrix error")
                console.error(err)
            }
        }
    }

    /**
     * Uses Quagga.js result to draw barcode's bounding box over screen 
     * @param result The result coming from quagga.js callback onProcessed
     */
    private draw1DBarcodeBounds(result) {
        console.info("CameraScanner: Drawing 1d barcode bounds...")
        this.clearDrawingContext()
        const drawingCtx = Quagga.canvas.ctx.overlay;
        if (result.boxes) {
            result.boxes
                .filter(box => {
                    return box !== result.box;
                })
                .forEach(box => {
                    Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
                        color: 'green',
                        lineWidth: 2,
                    });
                });
        }

        if (result.box) {
            Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
                color: '#00F',
                lineWidth: 2,
            });
        }

        if (result.codeResult && result.codeResult.code) {
            Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, {
                color: 'red',
                lineWidth: 3,
            });
        }
    }

    /**
     * Draws barcode bounds over screen (from Zxing data)
     */
    private draw2DBarcodeBounds(result) {
        console.info("CameraScanner: Drawing 2d barcode bounds...")
        const resultPoints = result.resultPoints;
        // 3 result points for a QRCode (3 finder patterns)
        if (resultPoints.length === 3) {
            this.clearDrawingContext()
            resultPoints.map(finderPattern => {
                this.drawFinderPatternMarker(finderPattern)
            })
        }
        // 4 result points for DataMatrix
        else if (resultPoints.length === 4) {
            this.drawDataMatrixBounds(result)
        }

    }

    /**
     * Draws a square over the finder patterns of the QRCode
     * @param finderPattern
     */
    private drawFinderPatternMarker(finderPattern) {
        const drawingCtx = Quagga.canvas.ctx.overlay;
        const finderPatternMarkerSize = 10;

        const finderPatternPath = [
            // top left corner of marker
            [finderPattern.x - finderPatternMarkerSize / 2,
            finderPattern.y - finderPatternMarkerSize / 2],
            // top right corner of marker
            [finderPattern.x + finderPatternMarkerSize / 2,
            finderPattern.y - finderPatternMarkerSize / 2],
            // bottom right corner of marker
            [finderPattern.x + finderPatternMarkerSize / 2,
            finderPattern.y + finderPatternMarkerSize / 2],
            // bottom left corner of marker
            [finderPattern.x - finderPatternMarkerSize / 2,
            finderPattern.y + finderPatternMarkerSize / 2]
        ]

        Quagga.ImageDebug.drawPath(
            finderPatternPath,
            { x: 0, y: 1 },
            drawingCtx, {
            color: 'yellow',
            lineWidth: 10,
        })
    }

    /**
     * Draws a square to materialize DataMatrix Bounds
     * @param result 
     */
    private drawDataMatrixBounds(result) {
        const boundsPoints = result.resultPoints;

        const drawingCtx = Quagga.canvas.ctx.overlay;
        // Drawing QRcode FinderPaterns
        const boundsPath = boundsPoints.map(
            point => [point.x, point.y]
        )

        Quagga.ImageDebug.drawPath(
            boundsPath,
            { x: 0, y: 1 },
            drawingCtx, {
            color: 'blue',
            lineWidth: 10,
        })
    }

    /**
     * Clears all previous drawings to prevent artefacts
     */
    private clearDrawingContext() {
        const drawingCtx = Quagga.canvas.ctx.overlay;
        const drawingCanvas = Quagga.canvas.dom.overlay;
        drawingCtx.clearRect(
            0,
            0,
            parseInt(drawingCanvas.getAttribute('width') ?? '', 10),
            parseInt(drawingCanvas.getAttribute('height') ?? '', 10),
        );
    }

    private async initDevices() {
        console.info("CameraScanner: initializing devices...")
        // Fetching preffered video device id from local storage
        const prefferedVideoDeviceId = localStorage.getItem(PREFERRED_VIDEO_DEVICE_ID);
        // Getting available hardware devices
        console.info("CameraScanner: enumerate devices...")
        const devices = await navigator.mediaDevices.enumerateDevices();

        //console.info("CameraScanner: devices: ", devices)
        console.info("CameraScanner: filtering devices...")
        // Filtering devices to eliminate no 'videoinput' devices, and front camera if possible
        // (there is no certain way to eliminate front camera at this point)
        this.videoDevices = devices.filter(device =>
            device.kind === "videoinput" &&
            (!device.label.includes("front") && !device.label.includes("Front"))
        );
        //console.info("CameraScanner.initDevices: filtered devices: ", this.videoDevices)

        console.info("CameraScanner: selecting device to use...")
        // If ids have changed (typically in Safari), reset preferred device id as it won't match any actual device)
        if (!this.videoDevices.find(device => device.deviceId === prefferedVideoDeviceId)) {
            console.info("CameraScanner: preferred video device not found, using random one")
            this.deviceToUseIndex = this.videoDevices.length - 1;
        } else {
            console.info("CameraScanner: using preferred video device")
            this.deviceToUseIndex = this.videoDevices.map(device => device.deviceId).indexOf(prefferedVideoDeviceId)
        }
        //console.info("CameraScanner.initDevices: device picked: ", this.videoDevices[this.deviceToUseIndex])

        console.info("CameraScanner: updating preferred video device")
        this.updatePreferredVideoDeviceIndex()
    }
}
